import { MAU_KHAC, MAU_KHO, MAU_LOAI_CHUNG_TU, MAU_PHONG_BAN, PHAM_VI_BOT, SO_BO_TOI_DA } from '../constants.ts';
import type { BarEntry, BaoCaoTho, BaoCaoTongHop } from '../types.ts';

/** Đếm theo khoá rồi sắp giảm dần; bằng nhau giữ thứ tự xuất hiện đầu tiên. */
export function demTheo<T>(ds: readonly T[], khoa: (x: T) => string): Array<[string, number]> {
  const dem = new Map<string, number>();
  for (const x of ds) dem.set(khoa(x), (dem.get(khoa(x)) ?? 0) + 1);
  return [...dem.entries()].sort((a, b) => b[1] - a[1]);
}

/** Độ rộng thanh (%), tối thiểu 3 để giá trị nhỏ vẫn thấy được. */
export function phanTramThanh(value: number, max: number): number {
  return max > 0 ? Math.max(3, Math.round((value / max) * 100)) : 3;
}

/** `t` đã dịch khoá; truyền vào để hàm này vẫn thuần và test được không cần React. */
export function tongHop(tho: BaoCaoTho, t: (khoa: string) => string, chuaPhanCong: string): BaoCaoTongHop {
  const theoLoai: BarEntry[] = demTheo(tho.loaiChungTu, (m) => m || 'OTHERS').map(([ma, value]) => ({
    nhan: ma in MAU_LOAI_CHUNG_TU ? t(`ocr.loai.${ma}`) : ma,
    value,
    mau: MAU_LOAI_CHUNG_TU[ma] ?? MAU_KHAC,
  }));

  const khoSapXep = [...tho.khoList].sort((a, b) => b.totalFiles - a.totalFiles);
  const theoBo: BarEntry[] = khoSapXep
    .slice(0, SO_BO_TOI_DA)
    .map((k) => ({ nhan: k.name, value: k.totalFiles, mau: MAU_KHO }));

  const theoPhamVi: BarEntry[] = demTheo(tho.chatbots, (b) => b.mode).map(([mode, value]) => {
    const cfg = PHAM_VI_BOT[mode];
    return { nhan: cfg ? t(cfg.khoa) : mode, value, mau: cfg?.mau ?? MAU_KHAC };
  });

  const tenPhong = new Map(tho.phongBan.map((p) => [p.id, p.name]));
  const theoPhongBan: BarEntry[] = demTheo(
    tho.nguoiDung,
    (u) => (u.departmentId ? tenPhong.get(u.departmentId) : undefined) ?? chuaPhanCong,
  ).map(([nhan, value], i) => ({ nhan, value, mau: MAU_PHONG_BAN[i % MAU_PHONG_BAN.length]! }));

  const mauOcr = tho.soMauOcr ?? 0;
  return {
    ocr: {
      mauOcr,
      tongChungTu: tho.chungTu?.total ?? 0,
      daXacNhan: tho.chungTu?.confirmed ?? 0,
      vaoKho: tho.chungTu?.transferred ?? 0,
      theoLoai,
    },
    kho: {
      boTriThuc: tho.khoStats?.tongBo ?? 0,
      tongTep: tho.khoStats?.tongTep ?? 0,
      phongBan: tho.khoStats?.phongBan ?? 0,
      tepPdf: tho.khoList.reduce((s, k) => s + (k.pdf ?? 0), 0),
      theoBo,
      // `max` tính trên danh sách đầy đủ, tối thiểu 1.
      maxBo: Math.max(1, ...khoSapXep.map((k) => k.totalFiles)),
    },
    chatbot: {
      tong: tho.chatbots.length,
      hoatDong: tho.chatbots.filter((b) => b.active).length,
      coKichBan: tho.chatbots.filter((b) => b.scenariosEnabled).length,
      // `answerable` do nền tảng tính, không suy lại ở client.
      traLoiDuoc: tho.chatbots.filter((b) => b.active && b.answerable).length,
      theoPhamVi,
    },
    heThong: {
      nguoiDung: tho.nguoiDung.length,
      hoatDong: tho.nguoiDung.filter((u) => u.active).length,
      vaiTro: tho.soVaiTro,
      mauOcr,
      theoPhongBan,
    },
    soLoi: tho.soLoi,
  };
}
